import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Plus, Pencil, Trash2, X, Loader2, ExternalLink, GripVertical } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get favicon from Google's favicon service
const getFavicon = (url) => {
  try {
    const domain = new URL(url.startsWith('http') ? url : `https://${url}`).hostname;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  } catch {
    return null;
  }
};

const normalizeUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `https://${url}`;
};

const AndGoPage = () => {
  const { getAuthHeaders } = useAuth();
  const [shortcuts, setShortcuts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingShortcut, setEditingShortcut] = useState(null);
  const [formData, setFormData] = useState({ name: '', url: '' });
  const [saving, setSaving] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [faviconErrors, setFaviconErrors] = useState({});
  const [urlPreview, setUrlPreview] = useState('');

  // Drag state
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  useEffect(() => { fetchShortcuts(); }, []);

  const fetchShortcuts = async () => {
    try {
      const res = await axios.get(`${API_URL}/shortcuts`, getAuthHeaders());
      setShortcuts(res.data || []);
    } catch {
      toast.error('Failed to load shortcuts');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.url.trim()) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/shortcuts`, formData, getAuthHeaders());
      setShortcuts(prev => [...prev, res.data]);
      setIsAddOpen(false);
      setFormData({ name: '', url: '' });
      setUrlPreview('');
      toast.success('Shortcut added!');
    } catch {
      toast.error('Failed to add shortcut');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editingShortcut) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/shortcuts/${editingShortcut.id}`, formData, getAuthHeaders());
      setShortcuts(prev => prev.map(s => s.id === editingShortcut.id ? res.data : s));
      setIsEditOpen(false);
      setEditingShortcut(null);
      setFaviconErrors(prev => { const n = {...prev}; delete n[editingShortcut.id]; return n; });
      toast.success('Updated!');
    } catch {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/shortcuts/${id}`, getAuthHeaders());
      setShortcuts(prev => prev.filter(s => s.id !== id));
      toast.success('Removed');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const openEdit = (shortcut) => {
    setEditingShortcut(shortcut);
    setFormData({ name: shortcut.name, url: shortcut.url });
    setUrlPreview(shortcut.url);
    setIsEditOpen(true);
  };

  // Drag and drop handlers
  const handleDragStart = (idx) => { dragItem.current = idx; };
  const handleDragEnter = (idx) => { dragOverItem.current = idx; };
  const handleDragEnd = async () => {
    const items = [...shortcuts];
    const draggedItem = items.splice(dragItem.current, 1)[0];
    items.splice(dragOverItem.current, 0, draggedItem);
    dragItem.current = null;
    dragOverItem.current = null;
    setShortcuts(items);
    try {
      await axios.put(`${API_URL}/shortcuts/reorder/batch`,
        { order: items.map(s => s.id) }, getAuthHeaders());
    } catch { /* silent */ }
  };

  const getFaviconLetter = (name) => name?.charAt(0)?.toUpperCase() || '?';
  const getColor = (name) => {
    const colors = ['#4f46e5','#0891b2','#059669','#d97706','#dc2626','#7c3aed','#db2777','#0284c7'];
    const idx = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[idx];
  };

  return (
    <div className="min-h-screen bg-slate-50 -m-4 lg:-m-8">
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white py-12 px-8 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #60a5fa 0%, transparent 50%), radial-gradient(circle at 80% 50%, #818cf8 0%, transparent 50%)' }} />
        <div className="relative">
          <h1 className="text-5xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            and<span className="text-blue-300">...</span>Go
          </h1>
          <p className="text-blue-200 text-base">Your favourite shortcuts, one click away</p>
        </div>
      </div>

      {/* Shortcuts Grid */}
      <div className="max-w-5xl mx-auto px-8 py-10">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="animate-spin text-slate-400" size={32} />
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-5">
              {shortcuts.map((shortcut, idx) => {
                const faviconUrl = getFavicon(shortcut.url);
                const hasFaviconError = faviconErrors[shortcut.id];
                const bg = getColor(shortcut.name);

                return (
                  <div
                    key={shortcut.id}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragEnter={() => handleDragEnter(idx)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => e.preventDefault()}
                    className="relative group"
                    onMouseEnter={() => setHoveredId(shortcut.id)}
                    onMouseLeave={() => setHoveredId(null)}
                  >
                    {/* Edit / Delete buttons on hover */}
                    {hoveredId === shortcut.id && (
                      <div className="absolute -top-2 -right-2 flex gap-1 z-10">
                        <button
                          onClick={(e) => { e.stopPropagation(); openEdit(shortcut); }}
                          className="w-6 h-6 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-slate-100 transition-colors border border-slate-200"
                          title="Edit"
                        >
                          <Pencil size={11} className="text-slate-600" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(shortcut.id); }}
                          className="w-6 h-6 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-red-50 transition-colors border border-slate-200"
                          title="Delete"
                        >
                          <X size={11} className="text-red-500" />
                        </button>
                      </div>
                    )}

                    {/* Card */}
                    <a
                      href={normalizeUrl(shortcut.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center justify-center w-28 h-28 bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer select-none"
                      title={shortcut.url}
                    >
                      {/* Icon / Favicon */}
                      {faviconUrl && !hasFaviconError ? (
                        <img
                          src={faviconUrl}
                          alt={shortcut.name}
                          className="w-12 h-12 object-contain rounded-xl mb-2"
                          onError={() => setFaviconErrors(prev => ({ ...prev, [shortcut.id]: true }))}
                        />
                      ) : (
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center mb-2 text-white text-xl font-bold shadow-inner"
                          style={{ backgroundColor: bg }}
                        >
                          {getFaviconLetter(shortcut.name)}
                        </div>
                      )}
                      <span className="text-xs font-medium text-slate-700 text-center px-2 leading-tight truncate w-full text-center">
                        {shortcut.name}
                      </span>
                    </a>

                    {/* Drag handle hint */}
                    {hoveredId === shortcut.id && (
                      <div className="absolute bottom-1 left-1/2 -translate-x-1/2">
                        <GripVertical size={12} className="text-slate-300" />
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Add shortcut card */}
              <button
                onClick={() => { setFormData({ name: '', url: '' }); setUrlPreview(''); setIsAddOpen(true); }}
                className="flex flex-col items-center justify-center w-28 h-28 rounded-2xl border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50/50 transition-all duration-200 text-slate-400 hover:text-blue-500"
              >
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center mb-2">
                  <Plus size={20} />
                </div>
                <span className="text-xs font-medium">Add shortcut</span>
              </button>
            </div>

            {shortcuts.length > 0 && (
              <p className="text-center text-xs text-slate-400 mt-8">
                Drag and drop to rearrange · Click to open in new tab
              </p>
            )}
          </>
        )}
      </div>

      {/* Floating + button */}
      <button
        onClick={() => { setFormData({ name: '', url: '' }); setUrlPreview(''); setIsAddOpen(true); }}
        className="fixed bottom-8 right-20 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-xl flex items-center justify-center transition-all hover:scale-110 z-20"
        title="Add shortcut"
      >
        <Plus size={24} />
      </button>

      {/* Add Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                <Plus size={16} className="text-white" />
              </div>
              Add Shortcut
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={formData.name}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. GitHub, Google, Telnyx"
                required
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>URL</Label>
              <Input
                value={formData.url}
                onChange={e => { setFormData(p => ({ ...p, url: e.target.value })); setUrlPreview(e.target.value); }}
                placeholder="e.g. github.com or https://github.com"
                required
              />
            </div>
            {/* Preview */}
            {urlPreview && (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                <img
                  src={getFavicon(urlPreview) || ''}
                  alt=""
                  className="w-8 h-8 object-contain rounded-lg"
                  onError={e => { e.target.style.display='none'; }}
                />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">{formData.name || 'Preview'}</p>
                  <p className="text-xs text-slate-400 truncate">{normalizeUrl(urlPreview)}</p>
                </div>
                <ExternalLink size={14} className="text-slate-400 flex-shrink-0" />
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setIsAddOpen(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={saving}>
                {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
                Add
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>Edit Shortcut</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={formData.name}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                required
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>URL</Label>
              <Input
                value={formData.url}
                onChange={e => { setFormData(p => ({ ...p, url: e.target.value })); setUrlPreview(e.target.value); }}
                required
              />
            </div>
            {urlPreview && (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                <img
                  src={getFavicon(urlPreview) || ''}
                  alt=""
                  className="w-8 h-8 object-contain rounded-lg"
                  onError={e => { e.target.style.display='none'; }}
                />
                <p className="text-xs text-slate-400 truncate flex-1">{normalizeUrl(urlPreview)}</p>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setIsEditOpen(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800" disabled={saving}>
                {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}
                Save
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AndGoPage;
